import { Chat as ChatIcon } from "@mui/icons-material";
import type { ColumnDef } from "@tanstack/react-table";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AIChat, { type AIChatHandle } from "~/components/aiChat";
import ChatHistoryPanel from "~/components/aiChat/components/ChatHistoryPanel";
import DiffViewer, { type DiffViewerHandle } from "~/components/aiChat/diff";
import CommonDocumentHeader from "~/components/Document/commonDocumentHeader";
import documentStyles from "~/components/Document/documentStyles.module.scss";
import ExportModal, { type TableExportData } from "~/components/Document/exportModal";
import { MarkdownContent, markdownContentClassName } from "~/components/Document/MarkdownContent";
import MarkdownEditorPreview from "~/components/Document/MarkdownEditorPreview";
import KnowledgeIndicator from "~/components/Project/knowledge/KnowledgeIndicator";
import KnowledgeSidebar from "~/components/Project/knowledge/KnowledgeSidebar";
import Button from "~/components/ui/button";
import Modal from "~/components/ui/modal";
import { TOAST_MESSAGES } from "~/constants/messages";
import FunctionalRequirementTable from "~/features/funcRequirement/functionalRequirementTable";
import { AIChatApi } from "~/functions/api/aiChat";
import { useNavigationPrompt } from "~/functions/navigationHooks";
import { useKnowledgeCount } from "~/hooks/useKnowledgeCount";
import { useMarkdownRenderer } from "~/hooks/useMarkdownRenderer";
import { useProjectMembers } from "~/hooks/useProjectMembers";
import { useProjectPermission } from "~/hooks/useProjectPermission";
import { useStructuredDocumentApply } from "~/hooks/useStructuredDocumentApply";
import DeleteIcon from "~/images/delete_row.svg?react";
import {
  acquireLock,
  addRow,
  clearError,
  deleteRow,
  fetchFunctionalRequirement,
  releaseLock,
  restoreFromCopyData,
  updateFunctionalRequirement,
  updateMarkdownContent,
  updateRow,
} from "~/slices/functionalRequirementSlice";
import { initializeSessions } from "~/slices/projectSlice";
import type { AppDispatch, RootState } from "~/store";
import chatStyles from "../systemProposal/styles.module.scss";
import styles from "./index.module.scss";

interface RowData {
  fid: number | string;
  業務: string;
  機能: string;
  利用ユーザー: string;
  実装方法: string;
  備考: string;
}

const convertJsonTableToMarkdown = (content: string): string => {
  try {
    const parsed: unknown = JSON.parse(content);
    if (
      parsed &&
      typeof parsed === "object" &&
      "headers" in parsed &&
      "rows" in parsed &&
      Array.isArray((parsed as { headers: unknown }).headers) &&
      Array.isArray((parsed as { rows: unknown }).rows)
    ) {
      const headers = (parsed as { headers: string[] }).headers;
      const rows = (parsed as { rows: (string | number)[][] }).rows;

      const escapeCell = (cell: string | number): string =>
        String(cell ?? "").replace(/\|/g, "\\|");

      const headerRow = `| ${headers.map(escapeCell).join(" | ")} |`;
      const separatorRow = `| ${headers.map(() => "---").join(" | ")} |`;
      const dataRows = rows.map((row) => `| ${row.map(escapeCell).join(" | ")} |`);

      return [headerRow, separatorRow, ...dataRows].join("\n");
    }
  } catch {
    // Not JSON — return as-is
  }
  return content;
};

function createRowData(
  fid: number | string,
  業務: string,
  機能: string,
  利用ユーザー: string,
  実装方法: string,
  備考: string,
): RowData {
  const rowData: RowData = {
    fid,
    業務,
    機能,
    利用ユーザー,
    実装方法,
    備考,
  };

  return rowData;
}

const FuncRequirement = () => {
  const { renderMarkdown } = useMarkdownRenderer();
  const dispatch = useDispatch<AppDispatch>();
  const { selectedId } = useParams<{ selectedId: string }>();

  const { error: projectError } = useProjectMembers();

  const { currentUser, currentProject } = useSelector((state: RootState) => state.auth);
  const { canAccessChat } = useProjectPermission();

  // チャットアイコンの表示可否を判定
  const shouldShowChatIcon = canAccessChat();

  const {
    data,
    isEditable,
    isLoading,
    error,
    documentInfo,
    isMarkdown,
    markdownContent,
    rawContent,
  } = useSelector((state: RootState) => state.functionalRequirement);
  const navigate = useNavigate();
  const [showExportModal, setShowExportModal] = React.useState(false);
  // const [initialData, setInitialData] = useState<typeof data>([]);

  // ナレッジインジケーター用のカウント取得
  const { count: knowledgeCount, totalCount: knowledgeTotalCount } =
    useKnowledgeCount("functional-requirements");
  // ナレッジサイドバーの状態
  const [isKnowledgeSidebarOpen, setIsKnowledgeSidebarOpen] = useState(false);
  // AIChat関連の状態
  const { currentSession } = useSelector((state: RootState) => state.project);
  const [showAIChat, setShowAIChat] = useState(true);
  const [isDiffModalOpen, setDiffModalOpen] = useState(false);
  const [showChatHistory, setShowChatHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [threads, setThreads] = useState<
    { id?: string; threadId?: string; title?: string; createdAt?: string; updatedAt?: string }[]
  >([]);
  const aiChatRef = React.useRef<AIChatHandle>(null);
  const diffViewerRef = React.useRef<DiffViewerHandle>(null);
  const [currentThreadId, setCurrentThreadId] = useState<string>("");
  const [documentBefore, setDocumentBefore] = useState<unknown>(null);
  const [documentAfter, setDocumentAfter] = useState<unknown>(null);
  const [isDiffSaving, setIsDiffSaving] = useState(false);
  const [isDiffValid, setIsDiffValid] = useState(true);
  const [diffViewerHeader, setDiffViewerHeader] = useState<React.ReactNode>(null);
  // 機能要件書はMarkdown形式に統一
  const isStructuredDocument = false;
  const handleStructuredDocumentApply = useStructuredDocumentApply({
    acquireLock: () => dispatch(acquireLock()).unwrap(),
    releaseLock: () => dispatch(releaseLock()),
    fetchDocument: () => dispatch(fetchFunctionalRequirement()),
    organizationId: currentProject?.id ? currentUser.organization_id : "",
    projectId: currentProject?.id || "",
    documentId: currentProject?.outputs?.["functional-requirements"]?.id || "",
    setIsDiffSaving,
    logPrefix: "FuncRequirement",
  });
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (projectError) {
      console.error("Project data loading error:", projectError);
      toast.error(TOAST_MESSAGES.DOCUMENT.PROJECT_LOAD_FAILED);
    }
  }, [projectError]);

  // Initialize session IDs (reinit on document change)
  const funcReqDocId = currentProject?.outputs?.["functional-requirements"]?.id;
  useEffect(() => {
    if (currentProject?.id && currentUser?.id) {
      dispatch(initializeSessions(funcReqDocId || ""));
    }
  }, [currentProject?.id, currentUser?.id, funcReqDocId, dispatch]);

  // currentSession.threadId が変更されたら currentThreadId を更新
  useEffect(() => {
    if (currentSession?.threadId) {
      setCurrentThreadId(currentSession.threadId);
    }
  }, [currentSession?.threadId]);

  // 履歴を更新する共通関数
  const refreshThreadHistory = async () => {
    // スレッド作成時や手動リロード時に履歴を更新
    setHistoryLoading(true);
    try {
      const api = new AIChatApi();
      const agentId = "commonAgent";
      const rid = currentSession?.resourceId;
      if (!rid) return;
      const list = await api.getThreads(agentId, rid, 10);
      setThreads(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("[FuncRequirement] Failed to refresh thread history:", e);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleApprovedDocumentChange = async (approvedDocument: string) => {
    setIsDiffSaving(true);
    try {
      await dispatch(acquireLock());
      dispatch(updateMarkdownContent(approvedDocument));
      await dispatch(updateFunctionalRequirement());
      await dispatch(fetchFunctionalRequirement());
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setIsDiffSaving(false);
    }
  };

  const handleDiffClose = () => {
    setIsDiffSaving(false);
    setDiffModalOpen(false);
    setShowAIChat(false);
    setDocumentBefore(null);
    setDocumentAfter(null);
    setDiffViewerHeader(null);
    setIsDiffValid(true);
  };

  const handleDiffApply = async () => {
    if (isDiffSaving) return;
    if (isStructuredDocument && documentAfter) {
      const content =
        typeof documentAfter === "string" ? documentAfter : JSON.stringify(documentAfter);
      const success = await handleStructuredDocumentApply(content);
      if (success) {
        handleDiffClose();
      }
    } else {
      void diffViewerRef.current?.applyChanges();
    }
  };

  const columns: ColumnDef<(typeof data)[0]>[] = [
    { accessorKey: "業務", header: "業務" },
    { accessorKey: "機能", header: "機能" },
    { accessorKey: "利用ユーザー", header: "利用ユーザー" },
    { accessorKey: "実装方法", header: "実装方法" },
    { accessorKey: "備考", header: "備考" },
    {
      accessorKey: "delete",
      header: "",
      cell: ({ row }) =>
        isEditable ? (
          <button
            type="button"
            onClick={() => handleDeleteRow(row.index)}
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <DeleteIcon width={16} height={16} />
          </button>
        ) : null,
    },
  ];

  const getExportData = (): TableExportData => {
    const name = "機能要件";

    if (isMarkdown) {
      return {
        headers: [],
        rows: [],
        name,
        markdownContent: markdownContent,
      };
    }

    const headers = ["業務", "機能", "利用ユーザー", "実装方法", "備考"];
    const rows = data.map((row) =>
      headers.map((header) => row[header as keyof RowData]?.toString() || ""),
    );
    return { headers, rows, name };
  };

  const toggleEditMode = async () => {
    if (isEditable) {
      await dispatch(updateFunctionalRequirement());
    } else {
      await dispatch(acquireLock());
    }
  };

  const handleAddNewRow = () => {
    if (isMarkdown) return;
    const newRow: RowData = createRowData((data.length + 1).toString(), "", "", "", "基本機能", "");
    dispatch(addRow(newRow));
  };

  const handleDeleteRow = (rowIndex: number) => {
    dispatch(deleteRow(rowIndex));
  };

  const handleUpdateData = (rowIndex: number, columnId: keyof (typeof data)[0], value: string) => {
    const updatedRow = { ...data[rowIndex]! } as RowData;
    if (columnId === "fid") {
      updatedRow[columnId] = parseInt(value, 10);
    } else {
      updatedRow[columnId] = value;
    }
    dispatch(updateRow({ index: rowIndex, data: updatedRow }));
  };

  const handleCancel = async () => {
    await dispatch(releaseLock());
    dispatch(restoreFromCopyData());
  };

  // 編集中の場合、ナビゲーションをブロック
  useNavigationPrompt(
    isEditable,
    "編集内容が保存されていません。ページを離れてもよろしいですか？",
    async () => {
      await dispatch(updateFunctionalRequirement());
    },
    handleCancel,
  );

  const handleExportClick = () => {
    setShowExportModal(true);
  };

  const handleHistoryClick = () => {
    const pathSegments = window.location.pathname.split("/");
    pathSegments.pop();
    navigate(`${pathSegments.join("/")}/history`);
  };

  useEffect(() => {
    if (!currentProject) {
      // プロジェクトが選択されていない場合はホームページにリダイレクト
      navigate(`/${currentUser.organization_id}/home`);
      return;
    }

    // URLパラメータから取得、なければReduxストアから取得
    const documentId = selectedId || currentProject?.outputs?.["functional-requirements"]?.id;

    if (!documentId) {
      toast.error(TOAST_MESSAGES.DOCUMENT.FUNC_REQUIREMENT_NOT_GENERATED);
      navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
      return;
    }

    dispatch(fetchFunctionalRequirement(documentId));
  }, [dispatch, currentProject, currentUser, navigate, selectedId]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isEditable) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isEditable]);

  if (isLoading) {
    return <div>ロード中...</div>;
  }

  const shouldUseMarkdownMode = isMarkdown;

  return (
    <div className={styles["funcRequirement"]}>
      {documentInfo && (
        <>
          <CommonDocumentHeader
            title="機能要件書"
            documentType="functional-requirements"
            createdAt={documentInfo.createdAt}
            modifiedAt={documentInfo.modifiedAt}
            {...(documentInfo.modifiedBy && { modifiedBy: documentInfo.modifiedBy })}
            isEditable={isEditable}
            isLoading={isLoading}
            handleCancel={handleCancel}
            toggleEditMode={toggleEditMode}
            handleExportClick={handleExportClick}
            handleHistoryClick={handleHistoryClick}
            showEditButton={true}
            showExportButton={true}
          />
          {false && (
            <div style={{ padding: "16px 24px", borderBottom: "1px solid #e5e7eb" }}>
              <KnowledgeIndicator
                documentType="functional-requirements"
                activeCount={knowledgeCount}
                totalCount={knowledgeTotalCount}
              />
            </div>
          )}
        </>
      )}
      {showExportModal && (
        <ExportModal
          documentType="functional-requirements"
          onClose={() => setShowExportModal(false)}
          exportData={{
            type: "table",
            exportData: getExportData(),
            name: "functional-requirement",
          }}
        />
      )}

      <div
        style={{
          display: "flex",
          gap: "24px",
          alignItems: "stretch",
          position: "relative",
          minHeight: "calc(100vh - 196px)",
        }}
      >
        <div style={{ flex: "1 1 auto" }}>
          {shouldUseMarkdownMode ? (
            <div className={styles["document-content"]}>
              <div className={styles["markdown-mode-content"]}>
                {isEditable ? (
                  <MarkdownEditorPreview
                    value={markdownContent}
                    onChange={(value) => dispatch(updateMarkdownContent(value))}
                    previewContent={renderMarkdown(markdownContent || "")}
                    textareaClassName={documentStyles["markdown-editor"] ?? ""}
                    previewClassName={markdownContentClassName}
                  />
                ) : (
                  <MarkdownContent>{renderMarkdown(markdownContent || "")}</MarkdownContent>
                )}
              </div>
            </div>
          ) : (
            <>
              <FunctionalRequirementTable
                data={data}
                columns={columns}
                updateData={handleUpdateData}
                isEditable={isEditable}
              />
              {isEditable && (
                <Button
                  label="追加"
                  onClick={handleAddNewRow}
                  styleType="add-row"
                  icon="addRowIcon"
                />
              )}
            </>
          )}
        </div>
        {!showAIChat && (
          <KnowledgeSidebar
            isOpen={isKnowledgeSidebarOpen}
            onClose={() => setIsKnowledgeSidebarOpen(false)}
            documentType="functional-requirements"
          />
        )}
        {showAIChat && (
          <div style={{ display: "flex", alignItems: "stretch", flex: "0 0 auto" }}>
            <ChatHistoryPanel
              open={showChatHistory}
              placement="sidebar"
              title="履歴（最新10件）"
              loading={historyLoading}
              threads={threads}
              activeThreadId={currentThreadId}
              onClose={() => setShowChatHistory(false)}
              onRefresh={refreshThreadHistory}
              onLoadThread={async (t) => {
                const tid = t.id || t.threadId;
                if (tid) {
                  setCurrentThreadId(tid);
                }
                await aiChatRef.current?.loadThread(t, "agent");
              }}
            />
            <AIChat
              ref={aiChatRef}
              isOpen={showAIChat}
              onClose={() => setShowAIChat(false)}
              onPreviewDiff={(before, after) => {
                setDocumentBefore(before);
                setDocumentAfter(after);
                setDiffModalOpen(before != null && after != null);
                setIsDiffValid(true);
              }}
              height="calc(100vh - 196px)"
              maxHeight="none"
              currentDocumentId={currentProject?.outputs?.["functional-requirements"]?.id || ""}
              currentProjectId={currentProject?.id || ""}
              currentDocumentType="functional-requirements"
              originalDocument={rawContent || markdownContent}
              threadId={currentSession?.threadId}
              resourceId={currentSession?.resourceId}
              onOpenHistory={async () => {
                setShowChatHistory(true);
                await refreshThreadHistory();
              }}
              onThreadUpdate={refreshThreadHistory}
              onThreadChange={(tid) => setCurrentThreadId(tid)}
            />
          </div>
        )}
      </div>

      {!showAIChat && !isEditable && !isKnowledgeSidebarOpen && shouldShowChatIcon && (
        <div className={chatStyles["floatingButtons"]}>
          <KnowledgeIndicator
            documentType="functional-requirements"
            activeCount={knowledgeCount}
            totalCount={knowledgeTotalCount}
            compact
            onOpen={() => setIsKnowledgeSidebarOpen(true)}
          />
          <button
            type="button"
            className={chatStyles["chatButton"]}
            onClick={() => setShowAIChat(true)}
            aria-label="AIチャットを開く"
          >
            <ChatIcon />
          </button>
        </div>
      )}

      <Modal
        label="差分プレビュー"
        isOpen={isDiffModalOpen && !!documentBefore && !!documentAfter}
        onClose={handleDiffClose}
        className="diffPreviewModal"
        usePortal
        subHeader={diffViewerHeader}
        content={
          !!documentBefore && !!documentAfter ? (
            <div className={chatStyles["diffModalContent"]}>
              <div className={chatStyles["diffModalScroll"]}>
                <DiffViewer
                  ref={diffViewerRef}
                  originalContent={convertJsonTableToMarkdown(
                    typeof documentBefore === "string"
                      ? documentBefore
                      : JSON.stringify(documentBefore, null, 2),
                  )}
                  updatedContent={convertJsonTableToMarkdown(
                    typeof documentAfter === "string"
                      ? documentAfter
                      : JSON.stringify(documentAfter, null, 2),
                  )}
                  onApprovedDocumentChange={handleApprovedDocumentChange}
                  onClose={handleDiffClose}
                  renderHeaderExternally
                  onHeaderChange={setDiffViewerHeader}
                />
              </div>
            </div>
          ) : null
        }
        footer={
          <div className={chatStyles["diffModalFooter"]}>
            <button
              type="button"
              className={`${chatStyles["diffFooterButton"]} ${chatStyles["diffFooterButtonSecondary"]}`}
              onClick={handleDiffClose}
              disabled={isDiffSaving}
            >
              キャンセル
            </button>
            <button
              type="button"
              className={`${chatStyles["diffFooterButton"]} ${chatStyles["diffFooterButtonPrimary"]}`}
              onClick={handleDiffApply}
              disabled={isDiffSaving || !documentBefore || !documentAfter || !isDiffValid}
            >
              反映する
            </button>
          </div>
        }
      />
    </div>
  );
};

export default FuncRequirement;
